import type { App } from '../../app/App';

/**
 * FPS / frame time / object counters. Updates the DOM twice a second only,
 * never per frame. Toggle with F3 or the ` key.
 */
export class DebugOverlay {
  private el: HTMLElement | null = null;
  private timer = 0;

  constructor(private readonly app: App) {
    window.addEventListener('keydown', (e) => {
      if (e.key === 'F3' || e.key === '`') {
        e.preventDefault();
        this.app.store.update(['settings'], (s) => (s.settings.debug = !s.settings.debug));
      }
    });
  }

  setVisible(on: boolean): void {
    if (on && !this.el) {
      this.el = document.createElement('pre');
      this.el.className = 'debug-overlay';
      this.el.setAttribute('aria-hidden', 'true');
      document.getElementById('overlay-layer')?.appendChild(this.el);
      this.timer = window.setInterval(() => this.refresh(), 500);
      this.refresh();
    } else if (!on && this.el) {
      window.clearInterval(this.timer);
      this.el.remove();
      this.el = null;
    }
  }

  private refresh(): void {
    if (!this.el) return;
    const { loop, pixi, tweens, assets, viewport, scenes } = this.app;
    const st = loop.stats;
    const tex = assets.stats;
    const mem = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
    this.el.textContent = [
      `FPS        ${st.fps.toFixed(1)}`,
      `frame      ${st.frameMs.toFixed(2)} ms`,
      `cpu work   ${st.workMs.toFixed(2)} ms (max ${st.maxWorkMs.toFixed(1)})`,
      `draw calls ${pixi.drawCalls < 0 ? 'n/a' : pixi.drawCalls}`,
      `objects    ${pixi.countObjects()}`,
      `tweens     ${tweens.count}`,
      `textures   atlas pages ${tex.atlasTextures} + dyn ${tex.dynamicTextures}`,
      `art        ${tex.placeholders ? `${tex.placeholders} PLACEHOLDER(S)` : 'all real'}${tex.problems ? `, ${tex.problems} problem(s)` : ''}`,
      `renderer   ${pixi.caps.renderer} (${pixi.caps.quality})`,
      `view       ${viewport.info.width}×${viewport.info.height} @${pixi.renderer.resolution.toFixed(2)}x · quality ${this.app.quality?.currentLevel ?? 0}`,
      `scene      ${scenes.currentName ?? '-'}`,
      mem ? `heap       ${(mem.usedJSHeapSize / 1048576).toFixed(1)} MB` : '',
    ]
      .filter(Boolean)
      .join('\n');
  }
}
